import React from 'react';
import { Box, Typography, Container, Divider } from '@mui/material';
import { Link } from 'react-router-dom';

import { NAV_BG, AMBER } from '../theme';

const Section = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 1.5 }}>
            {title}
        </Typography>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', color: '#475569', lineHeight: 1.8 }}>
            {children}
        </Typography>
    </Box>
);

const PrivacyPage = () => (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f1f5f9', py: 6 }}>
        <Container maxWidth="md">
            <Box sx={{ backgroundColor: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', boxShadow: '0 8px 40px rgba(15,45,94,0.08)', p: { xs: 3, md: 6 } }}>

                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '2rem', color: NAV_BG, mb: 1 }}>
                        Privacy Policy
                    </Typography>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>
                        Last updated: May 2026
                    </Typography>
                </Box>

                <Divider sx={{ mb: 4, borderColor: '#e2e8f0' }} />

                <Section title="1. Introduction">
                    SchoolByBus ("we", "our", or "us") is committed to protecting the privacy of all users of our school bus management platform, including the SBB Driver mobile application. This Privacy Policy explains how we collect, use, and protect personal information in accordance with the General Data Protection Regulation (GDPR) and applicable Greek law.
                </Section>

                <Section title="2. Data Controller">
                    SchoolByBus acts as the data controller for personal data collected through this platform and the SBB Driver app. The school institution operating the platform may act as a joint controller for the personal data of their students and staff. For data protection inquiries, contact us at sbnextgenapp@gmail.com.
                </Section>

                <Section title="3. Information We Collect">
                    We collect the following categories of personal data:{'\n\n'}
                    • <strong>Account information:</strong> Name, email address, phone number, and role (parent/driver/administrator).{'\n'}
                    • <strong>Student information:</strong> Student name, class, assigned bus stop, attendance records, and meal data (managed by school staff).{'\n'}
                    • <strong>Location data:</strong> GPS coordinates collected from driver devices while a route is active.{'\n'}
                    • <strong>Usage data:</strong> App interactions, login timestamps, and message history.{'\n'}
                    • <strong>Device information:</strong> Push notification tokens for delivery of alerts.
                </Section>

                <Section title="4. How We Use Your Information">
                    We use collected information solely for the following purposes:{'\n\n'}
                    • Providing real-time bus tracking to parents and school staff{'\n'}
                    • Managing route assignments and student pickup/drop-off{'\n'}
                    • Sending push notifications about bus arrival and delays{'\n'}
                    • Enabling communication between school administration and parents{'\n'}
                    • Generating attendance and transport reports for the school{'\n'}
                    • Maintaining account security and preventing unauthorized access
                </Section>

                <Section title="5. Legal Basis for Processing">
                    We process personal data on the following legal bases: (a) Performance of a contract — to provide the school bus management service; (b) Legitimate interests — to ensure the safety and security of students during transport; (c) Consent — for optional features such as push notifications and parent-to-parent messaging.
                </Section>

                <Section title="6. Data Sharing">
                    We do not sell or share personal data with third parties for commercial purposes. Data may be shared only:{'\n\n'}
                    • With school staff who need it to provide the service{'\n'}
                    • With parents regarding their own children's information{'\n'}
                    • With infrastructure providers (hosting) under strict data processing agreements{'\n'}
                    • When required by law or court order
                </Section>

                <Section title="7. Location Data">
                    Driver location data is collected only while a route is marked as active in the app. Location data is visible to school administrators and parents of students on the active route. Location history is stored for a maximum of 30 days for operational review purposes.
                </Section>

                <Section title="8. Data Retention">
                    Personal data is retained for the duration of the user's relationship with the school plus any period required by applicable law. Student transport records are retained for the current academic year. You may request deletion of your data by contacting the school administrator, subject to legal retention requirements.
                </Section>

                <Section title="9. Your Rights (GDPR)">
                    Under GDPR, you have the following rights regarding your personal data:{'\n\n'}
                    • <strong>Access:</strong> Request a copy of your personal data{'\n'}
                    • <strong>Rectification:</strong> Correct inaccurate or incomplete data{'\n'}
                    • <strong>Erasure:</strong> Request deletion of your data ("right to be forgotten"){'\n'}
                    • <strong>Restriction:</strong> Limit how your data is processed{'\n'}
                    • <strong>Portability:</strong> Receive your data in a machine-readable format{'\n'}
                    • <strong>Objection:</strong> Object to certain types of processing{'\n\n'}
                    To exercise these rights, contact your school administrator.
                </Section>

                <Section title="10. Data Security">
                    We implement appropriate technical and organizational measures to protect personal data including encrypted data transmission (HTTPS), JWT-based authentication with token expiry, access controls limiting data visibility by role, and regular security reviews.
                </Section>

                <Section title="11. Children's Privacy">
                    Our service processes data about minors (students) under the direction and responsibility of the school. Parents may access information about their own children through the Parent Portal. We do not knowingly allow minors direct access to the platform.
                </Section>

                <Section title="12. Changes to This Policy">
                    We may update this Privacy Policy to reflect changes in our practices or applicable law. We will notify users of significant changes through the application. Continued use of the Service constitutes acceptance of the updated policy.
                </Section>

                <Section title="13. Contact & Complaints">
                    For privacy-related inquiries, contact SchoolByBus at sbnextgenapp@gmail.com. You also have the right to lodge a complaint with the Hellenic Data Protection Authority (HDPA) at www.dpa.gr.
                </Section>

                <Divider sx={{ mb: 3, borderColor: '#e2e8f0' }} />

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Link to="/terms" style={{ color: AMBER, fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none' }}>
                        Terms & Conditions →
                    </Link>
                    <Link to="/login" style={{ color: '#94a3b8', fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', textDecoration: 'none' }}>
                        ← Back to Login
                    </Link>
                </Box>
            </Box>
        </Container>
    </Box>
);

export default PrivacyPage;
